"use client";;
import * as React from "react";
import { DataGridCell } from "@/components/data-grid/data-grid-cell";
import { useComposedRefs } from "@/lib/compose-refs";
import {
  flexRender,
  getCellKey,
  getCommonPinningStyles,
  getRowHeightValue,
} from "@/lib/data-grid";
import { cn } from "@/lib/utils/index";

export const DataGridRow = React.memo(DataGridRowImpl, (prev, next) => {
  const prevRowIndex = prev.virtualItem.index;
  const nextRowIndex = next.virtualItem.index;

  // Re-render if row identity changed
  if (prev.row.id !== next.row.id) {
    return false;
  }

  // Re-render if row data (original) reference changed
  if (prev.row.original !== next.row.original) {
    return false;
  }

  // Re-render if virtual position changed (handles transform updates)
  if (prev.virtualItem.start !== next.virtualItem.start) {
    return false;
  }

  // Re-render if focus state changed for this row
  const prevHasFocus = prev.focusedCell?.rowIndex === prevRowIndex;
  const nextHasFocus = next.focusedCell?.rowIndex === nextRowIndex;

  if (prevHasFocus !== nextHasFocus) {
    return false;
  }

  // Re-render if focused column changed within this row
  if (nextHasFocus && prevHasFocus) {
    if (prev.focusedCell?.columnId !== next.focusedCell?.columnId) {
      return false;
    }
  }

  // Re-render if editing state changed for this row
  const prevHasEditing = prev.editingCell?.rowIndex === prevRowIndex;
  const nextHasEditing = next.editingCell?.rowIndex === nextRowIndex;

  if (prevHasEditing !== nextHasEditing) {
    return false;
  }

  // Re-render if editing column changed within this row
  if (nextHasEditing && prevHasEditing) {
    if (prev.editingCell?.columnId !== next.editingCell?.columnId) {
      return false;
    }
  }

  // Re-render if this row's selected cells changed
  // Using stable Set reference that only includes this row's cells
  if (prev.cellSelectionKeys !== next.cellSelectionKeys) {
    return false;
  }

  // Re-render if column visibility changed
  if (prev.columnVisibility !== next.columnVisibility) {
    return false;
  }

  // Re-render if row height changed
  if (prev.rowHeight !== next.rowHeight) {
    return false;
  }

  // Re-render if column pinning state changed
  if (prev.columnPinning !== next.columnPinning) {
    return false;
  }

  // Re-render if readOnly changed
  if (prev.readOnly !== next.readOnly) {
    return false;
  }

  // Re-render if search match columns changed for this row
  if (prev.searchMatchColumns !== next.searchMatchColumns) {
    return false;
  }

  // Re-render if active search match changed for this row
  if (prev.activeSearchMatch?.columnId !== next.activeSearchMatch?.columnId) {
    return false;
  }

  // Skip re-render - props are equal
  return true;
});

function DataGridRowImpl(
  {
    row,
    tableMeta,
    virtualItem,
    measureElement,
    rowMapRef,
    rowHeight,
    columnVisibility,
    columnPinning,
    focusedCell,
    editingCell,
    cellSelectionKeys,
    searchMatchColumns,
    activeSearchMatch,
    dir,
    readOnly,
    stretchColumns,
    className,
    style,
    ref,
    ...props
  }
) {
  const virtualRowIndex = virtualItem.index;

  const onRowChange = React.useCallback((node) => {
    if (typeof virtualRowIndex === "undefined") return;

    if (node) {
      measureElement(node);
      rowMapRef.current?.set(virtualRowIndex, node);
    } else {
      rowMapRef.current?.delete(virtualRowIndex);
    }
  }, [virtualRowIndex, measureElement, rowMapRef]);

  const rowRef = useComposedRefs(ref, onRowChange);

  const isRowSelected = row.getIsSelected();

  // Memoize visible cells to avoid recreating cell array on every render
  // Though TanStack returns new Cell wrappers, memoizing the array helps React's reconciliation
  // biome-ignore lint/correctness/useExhaustiveDependencies: columnVisibility and columnPinning are used for calculating the visible cells
  const visibleCells = React.useMemo(() => row.getVisibleCells(), [row, columnVisibility, columnPinning]);

  return (
    <div
      key={row.id}
      role="row"
      aria-rowindex={virtualRowIndex + 2}
      aria-selected={isRowSelected}
      data-index={virtualRowIndex}
      data-slot="grid-row"
      tabIndex={-1}
      {...props}
      ref={rowRef}
      className={cn(
        "absolute flex w-full border-b will-change-transform",
        // subtle striping + hover sheen (keeps brand feel: minimal, not loud)
        virtualRowIndex % 2 === 0 ? "bg-background/40" : "bg-muted/20",
        "hover:bg-primary/5",
        "hover:shadow-[inset_0_1px_0_rgba(255,255,255,0.18)]",
        className
      )}
      style={{
        height: `${getRowHeightValue(rowHeight)}px`,
        transform: `translateY(${virtualItem.start}px)`,
        ...style,
      }}>
      {visibleCells.map((cell, colIndex) => {
        const columnId = cell.column.id;

        const isCellFocused =
          focusedCell?.rowIndex === virtualRowIndex &&
          focusedCell?.columnId === columnId;
        const isCellEditing =
          editingCell?.rowIndex === virtualRowIndex &&
          editingCell?.columnId === columnId;
        const isCellSelected =
          cellSelectionKeys?.has(getCellKey(virtualRowIndex, columnId)) ??
          false;

        const isSearchMatch = searchMatchColumns?.has(columnId) ?? false;
        const isActiveSearchMatch = activeSearchMatch?.columnId === columnId;

        return (
          <div
            key={cell.id}
            role="gridcell"
            aria-colindex={colIndex + 1}
            data-highlighted={isCellFocused ? "" : undefined}
            data-slot="grid-cell"
            tabIndex={-1}
            className={cn("shrink-0", {
              grow: stretchColumns && columnId !== "select",
              "border-e": columnId !== "select",
            })}
            style={{
              ...getCommonPinningStyles({ column: cell.column, dir, withBorder: true }),
              width: `calc(var(--col-${columnId}-size) * 1px)`,
            }}>
            {typeof cell.column.columnDef.header === "function" ? (
              <div
                className={cn("size-full px-3 py-1.5", {
                  "bg-primary/10": isRowSelected,
                })}>
                {flexRender(cell.column.columnDef.cell, cell.getContext())}
              </div>
            ) : (
              <DataGridCell
                cell={cell}
                tableMeta={tableMeta}
                rowIndex={virtualRowIndex}
                columnId={columnId}
                rowHeight={rowHeight}
                isFocused={isCellFocused}
                isEditing={isCellEditing}
                isSelected={isCellSelected}
                isSearchMatch={isSearchMatch}
                isActiveSearchMatch={isActiveSearchMatch}
                readOnly={readOnly} />
            )}
          </div>
        );
      })}
    </div>
  );
}
